import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useMemo, useState } from 'react';

import { fmt, fmtB, fmtDate } from '@dcc-erp/shared';
import type {
  CreateWhtCertificate,
  CreateWhtFiling,
  WhtCertificate,
  WhtCertStatus,
  WhtFiling,
  WhtFormType,
  Branch,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { BranchSelect } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { useActiveCompanyId, useBranches } from '../lib/queries';
import {
  downloadCertPdf,
  downloadFilingTxt,
  useConfirmWhtRemittance,
  useCreateWhtCert,
  useCreateWhtFiling,
  useFileWhtBranches,
  useWhtCerts,
  useWhtFilings,
} from '../lib/queries/wht';

/* ===== Types — UI mirrors the prototype shape; the API delivers WhtCertificate ===== */
type UiForm = '3' | '53' | '54';
type UiStatus = 'issued' | 'pending' | 'filed' | 'void';
type WhtTab = 'certs' | 'pnd3' | 'pnd53' | 'pnd54' | 'rates';
type WhtIssueType = 'corp' | 'indiv' | 'foreign';

interface WhtRateInfo {
  code: string;
  label: string;
  rate: number;
  income: string;
  form: UiForm;
  forIndiv: string;
  desc: string;
}

interface UiCert {
  id: string; // backend uuid
  branchId: string | null;
  voucherNo: string;
  date: string;
  vendor: string;
  taxId: string;
  addr: string;
  cat: string;
  label: string;
  base: number;
  rate: number;
  wht: number;
  paymentRef: string;
  form: UiForm;
  status: UiStatus;
  indiv: boolean;
  // B4: e-WHT certs are issued/remitted by the bank — no in-house 50ทวิ printout.
  channel: 'NORMAL' | 'E_WHT';
  // B5: bank-remittance confirmation fields (null = not yet confirmed).
  bankConfirmedAt: string | null;
  bankRemittanceRef: string | null;
}

interface WhtTotalsBucket {
  base: number;
  wht: number;
  count: number;
}

interface WhtTotals {
  all: WhtTotalsBucket;
  f3: WhtTotalsBucket;
  f53: WhtTotalsBucket;
  f54: WhtTotalsBucket;
  pending: WhtTotalsBucket;
}

interface PndCfg {
  title: string;
  sub: string;
  taxIdLabel: string;
}

interface ByTypeBucket {
  label: string;
  count: number;
  base: number;
  wht: number;
}

/* ===== Rates reference — static (mirrors prototype + DB seed for incomeType) ===== */
const WHT_RATES: readonly WhtRateInfo[] = [
  {
    code: 'SVC',
    label: 'ค่าบริการทั่วไป',
    rate: 3.0,
    income: 'มาตรา 40(2)/(8)',
    form: '53',
    forIndiv: '3',
    desc: 'ค่าจ้างทำของ / บริการที่ไม่เข้าหมวดอื่น',
  },
  {
    code: 'PROF',
    label: 'ค่าบริการวิชาชีพอิสระ',
    rate: 3.0,
    income: 'มาตรา 40(6)',
    form: '53',
    forIndiv: '3',
    desc: 'ทนายความ บัญชี วิศวกร แพทย์ สถาปนิก',
  },
  {
    code: 'RENT',
    label: 'ค่าเช่าทรัพย์สิน',
    rate: 5.0,
    income: 'มาตรา 40(5)',
    form: '53',
    forIndiv: '3',
    desc: 'ค่าเช่าอาคาร โกดัง รถ อุปกรณ์',
  },
  {
    code: 'ADS',
    label: 'ค่าโฆษณา',
    rate: 2.0,
    income: 'มาตรา 40(8)',
    form: '53',
    forIndiv: '3',
    desc: 'ค่าโฆษณา TV, สื่อสิ่งพิมพ์, ออนไลน์, Influencer',
  },
  {
    code: 'XPORT',
    label: 'ค่าขนส่ง (ในประเทศ)',
    rate: 1.0,
    income: 'มาตรา 40(8)',
    form: '53',
    forIndiv: '3',
    desc: 'ค่าขนส่งสินค้า/บุคคล ในไทย',
  },
  {
    code: 'DIV',
    label: 'เงินปันผล',
    rate: 10.0,
    income: 'มาตรา 40(4)(ข)',
    form: '53',
    forIndiv: '2',
    desc: 'จ่ายเงินปันผลให้ผู้ถือหุ้น',
  },
  {
    code: 'INT',
    label: 'ดอกเบี้ย (นิติบุคคล)',
    rate: 1.0,
    income: 'มาตรา 40(4)(ก)',
    form: '53',
    forIndiv: '2',
    desc: 'ดอกเบี้ยเงินกู้/พันธบัตร',
  },
  {
    code: 'COMM',
    label: 'ค่านายหน้า / Commission',
    rate: 3.0,
    income: 'มาตรา 40(2)',
    form: '53',
    forIndiv: '3',
    desc: 'ค่านายหน้าขายสินค้า/บริการ',
  },
  {
    code: 'PRIZE',
    label: 'รางวัล ส่วนลด',
    rate: 5.0,
    income: 'มาตรา 40(8)',
    form: '53',
    forIndiv: '3',
    desc: 'รางวัลชิงโชค ส่วนลดจูงใจ',
  },
  {
    code: 'FOREIGN',
    label: 'จ่ายไปต่างประเทศ',
    rate: 15.0,
    income: 'มาตรา 70',
    form: '54',
    forIndiv: '54',
    desc: 'ค่าบริการ ค่าสิทธิ์ ดอกเบี้ย ที่จ่ายให้ผู้ไม่มีถิ่นในไทย',
  },
];

const FALLBACK_RATE: WhtRateInfo = WHT_RATES[0]!;

function getRateInfo(code: string): WhtRateInfo {
  return WHT_RATES.find((x) => x.code === code) ?? FALLBACK_RATE;
}

// Official ภ.ง.ด. header line "สาขาที่ {taxBranch} ({nameTh})" resolved from the
// cert's branchId against the company branch master. Falls back to the head
// office wording when no branch is set.
function branchHeader(branchId: string | null, branches: readonly Branch[]): string {
  if (branchId) {
    const b = branches.find((x) => x.id === branchId);
    if (b) return `สาขาที่ ${b.taxBranch ?? '00000'} (${b.nameTh})`;
  }
  return 'สาขาที่ 00000 (สำนักงานใหญ่)';
}

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

const PND_CFGS: Record<UiForm, PndCfg> = {
  '3': {
    title: 'ภ.ง.ด. 3 — แบบยื่นรายการภาษีเงินได้หัก ณ ที่จ่าย (บุคคลธรรมดา)',
    sub: 'สำหรับการจ่ายเงินได้ตามมาตรา 40(2) – 40(8) ให้บุคคลธรรมดา',
    taxIdLabel: 'เลขประจำตัวประชาชน 13 หลัก',
  },
  '53': {
    title: 'ภ.ง.ด. 53 — แบบยื่นรายการภาษีเงินได้หัก ณ ที่จ่าย (นิติบุคคล)',
    sub: 'สำหรับการจ่ายเงินได้ให้บริษัท ห้างหุ้นส่วน หรือนิติบุคคลอื่น',
    taxIdLabel: 'เลขประจำตัวผู้เสียภาษี 13 หลัก',
  },
  '54': {
    title: 'ภ.ง.ด. 54 — แบบนำส่งภาษีเงินได้หัก ณ ที่จ่ายจากต่างประเทศ',
    sub: 'สำหรับการจ่ายเงินได้ให้ผู้ไม่มีถิ่นที่อยู่ในประเทศไทย (มาตรา 70)',
    taxIdLabel: 'เลขประจำตัวผู้เสียภาษีต่างประเทศ',
  },
};

// PND53 = corp (default), PND3 = individual, PND54 = foreign payment
const FORM_TO_UI: Record<WhtFormType, UiForm> = { PND3: '3', PND53: '53', PND54: '54' };
const UI_TO_FORM: Record<UiForm, WhtFormType> = { '3': 'PND3', '53': 'PND53', '54': 'PND54' };

const STATUS_TO_UI: Record<WhtCertStatus, UiStatus> = {
  DRAFT: 'pending',
  ISSUED: 'issued',
  FILED: 'filed',
  VOID: 'void',
};

function toUiCert(c: WhtCertificate): UiCert {
  const form = FORM_TO_UI[c.formType];
  const base = Number(c.base);
  const rate = Number(c.rate);
  const amount = Number(c.amount);
  const dateStr =
    c.certDate instanceof Date
      ? c.certDate.toISOString().slice(0, 10)
      : String(c.certDate).slice(0, 10);
  return {
    id: c.id,
    branchId: c.branchId ?? null,
    voucherNo: c.voucherNo,
    date: dateStr,
    vendor: c.vendorName,
    taxId: c.vendorTaxId,
    addr: c.vendorAddress ?? '',
    cat: c.incomeType,
    label: getRateInfo(c.incomeType).label,
    base,
    rate,
    wht: amount,
    paymentRef: c.paymentRef,
    form,
    status: STATUS_TO_UI[c.status],
    indiv: form === '3',
    channel: c.channel ?? 'NORMAL',
    bankConfirmedAt: c.bankConfirmedAt
      ? c.bankConfirmedAt instanceof Date
        ? c.bankConfirmedAt.toISOString().slice(0, 10)
        : String(c.bankConfirmedAt).slice(0, 10)
      : null,
    bankRemittanceRef: c.bankRemittanceRef ?? null,
  };
}

function sumBucket(list: readonly UiCert[]): WhtTotalsBucket {
  return {
    base: list.reduce((a, r) => a + r.base, 0),
    wht: list.reduce((a, r) => a + r.wht, 0),
    count: list.length,
  };
}

// Default period — current month 'YYYYMM'. Prototype shows พ.ค. 2026 = '202605'.
function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/* ===== Route ===== */
export const Route = createFileRoute('/wht')({
  component: WhtPage,
});

export function WhtPage() {
  const companyId = useActiveCompanyId();
  const [period, setPeriod] = useState<string>(currentPeriod());
  const [tab, setTab] = useState<WhtTab>('certs');
  const [q, setQ] = useState<string>('');
  const [formF, setFormF] = useState<'all' | UiForm>('all');
  const [statusF, setStatusF] = useState<'all' | UiStatus>('all');
  const [showIssue, setShowIssue] = useState<boolean>(false);

  const certsQuery = useWhtCerts(companyId, { period });
  const filingsQuery = useWhtFilings(companyId);
  const branchesQuery = useBranches(companyId);
  const branches = useMemo<Branch[]>(() => branchesQuery.data ?? [], [branchesQuery.data]);
  const createCert = useCreateWhtCert(companyId);
  const createFiling = useCreateWhtFiling(companyId);
  const confirmRemit = useConfirmWhtRemittance(companyId);
  const fileBranches = useFileWhtBranches(companyId);

  const allCerts = useMemo<UiCert[]>(
    () => (certsQuery.data ?? []).map(toUiCert),
    [certsQuery.data],
  );

  // filingCerts = certs that will appear on the actual ภ.ง.ด. return (e-WHT is
  // excluded because the bank files those directly — matching the server's
  // summarize() / exportFilingTxt exclusion per D13).
  const filingCerts = useMemo<UiCert[]>(
    () => allCerts.filter((r) => r.channel !== 'E_WHT'),
    [allCerts],
  );

  const filtered = useMemo<UiCert[]>(
    () =>
      allCerts.filter((r) => {
        if (formF !== 'all' && r.form !== formF) return false;
        if (statusF !== 'all' && r.status !== statusF) return false;
        if (
          q &&
          !(
            r.voucherNo.toLowerCase().includes(q.toLowerCase()) ||
            r.vendor.toLowerCase().includes(q.toLowerCase()) ||
            r.taxId.includes(q)
          )
        )
          return false;
        return true;
      }),
    [allCerts, q, formF, statusF],
  );

  // totals for the ภ.ง.ด. filing preview use filingCerts (e-WHT excluded) so
  // the on-screen numbers match what is actually submitted to the RD.
  const totals = useMemo<WhtTotals>(
    () => ({
      all: sumBucket(allCerts), // "all certs issued" — intentionally inclusive
      f3: sumBucket(filingCerts.filter((r) => r.form === '3')),
      f53: sumBucket(filingCerts.filter((r) => r.form === '53')),
      f54: sumBucket(filingCerts.filter((r) => r.form === '54')),
      pending: sumBucket(allCerts.filter((r) => r.status === 'pending')),
    }),
    [allCerts, filingCerts],
  );

  const handleFile = (form: UiForm) => {
    if (!companyId) return;
    const target = allCerts.filter((r) => r.form === form && r.status === 'issued');
    if (target.length === 0) {
      window.alert(
        `ไม่มีหนังสือ ${form === '3' ? 'ภ.ง.ด. 3' : form === '53' ? 'ภ.ง.ด. 53' : 'ภ.ง.ด. 54'} สถานะ ISSUED ในงวดนี้`,
      );
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const payload: CreateWhtFiling = {
      period,
      formType: UI_TO_FORM[form],
      filingDate: today,
    };
    createFiling.mutate(payload);
  };

  // ยื่นแยกสาขา — one ภ.ง.ด. filing per tax-branch with certs this period.
  const handleFileBranches = (form: UiForm) => {
    if (!companyId) return;
    fileBranches.mutate({ period, formType: UI_TO_FORM[form] });
  };

  // B4: download the RD ภ.ง.ด. e-filing TXT for a form's consolidated filing.
  // Resolve the (period, formType, no-branch) consolidated filing; fall back to
  // the FIRST filing of that form when none matches the period, so the action
  // stays usable regardless of which month is selected.
  const handleExportTxt = (form: UiForm) => {
    if (!companyId) return;
    const formType = UI_TO_FORM[form];
    const all = filingsQuery.data ?? [];
    const f =
      all.find((x) => x.period === period && x.formType === formType && !x.branchId) ??
      all.find((x) => x.formType === formType);
    if (f) void downloadFilingTxt(companyId, f.id);
    else
      window.alert(
        `ยังไม่มีแบบ ${form === '3' ? 'ภ.ง.ด. 3' : form === '53' ? 'ภ.ง.ด. 53' : 'ภ.ง.ด. 54'} ที่ยื่นในงวดนี้ — กดยื่นก่อน`,
      );
  };

  // B4: open the 50ทวิ PDF for a cert in a new tab.
  const handlePrintPdf = (certId: string) => {
    if (companyId) void downloadCertPdf(companyId, certId);
  };

  // B5: confirm bank e-WHT remittance (posts Dr WHT_PAYABLE / Cr bank JV).
  const handleConfirmRemit = (
    certId: string,
    data: { bankRemittanceRef: string; bankConfirmedAt: string; cashAccountCode: string },
  ) => {
    confirmRemit.mutate({ id: certId, data });
  };

  const tabs: readonly (readonly [WhtTab, string])[] = [
    ['certs', 'หนังสือรับรอง WHT (ทั้งหมด)'],
    ['pnd3', 'ภ.ง.ด. 3 (บุคคล)'],
    ['pnd53', 'ภ.ง.ด. 53 (นิติบุคคล)'],
    ['pnd54', 'ภ.ง.ด. 54 (ต่างประเทศ)'],
    ['rates', 'อัตราภาษีหัก ณ ที่จ่าย'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'ภาษีหัก ณ ที่จ่าย']}
        title={`ภาษีหัก ณ ที่จ่าย — ภ.ง.ด. 3 / 53 / 54 · ${period}`}
        actions={
          <>
            <HelpButton guide="wht" />
            <select className="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="202605">งวด: พ.ค. 2026</option>
              <option value="202604">งวด: เม.ย. 2026</option>
              <option value="202603">งวด: มี.ค. 2026</option>
            </select>
            <button className="btn" disabled={!companyId} onClick={() => handleExportTxt('53')}>
              <Icon name="download" size={14} /> Export ภ.ง.ด.
            </button>
            <button className="btn primary" onClick={() => setShowIssue(true)}>
              <Icon name="plus" size={14} /> ออกหนังสือรับรอง
            </button>
          </>
        }
      />

      {/* Filing reminder banner */}
      <div
        style={{
          background: 'linear-gradient(90deg, var(--brand-tint), white 70%)',
          border: '1px solid var(--brand-tint-2)',
          borderLeft: '4px solid var(--brand)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
          display: 'flex',
          alignItems: 'center',
          gap: 18,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'var(--brand)',
            color: 'white',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="calendar" size={22} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-display)' }}>
            ครบกำหนดยื่นภ.ง.ด.เดือน พ.ค. 2026:{' '}
            <span style={{ color: 'var(--brand)' }}>7 มิ.ย. 2026</span> · เหลืออีก 15 วัน
          </div>
          <div className="meta" style={{ marginTop: 2 }}>
            ภ.ง.ด. 3: {fmt(totals.f3.count)} ฉบับ · ภาษีรวม {fmt(totals.f3.wht)} บาท · ภ.ง.ด. 53:{' '}
            {fmt(totals.f53.count)} ฉบับ · ภาษีรวม {fmt(totals.f53.wht)} บาท · ภ.ง.ด. 54:{' '}
            {fmt(totals.f54.count)} ฉบับ · ภาษีรวม {fmt(totals.f54.wht)} บาท
          </div>
        </div>
        <button
          className="btn primary"
          disabled={createFiling.isPending}
          onClick={() => handleFile('53')}
        >
          <Icon name="upload" size={14} /> ยื่นแบบออนไลน์
        </button>
      </div>

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ฐานเงินที่จ่ายรวม
          </div>
          <div className="value tnum">
            {fmtB(totals.all.base)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{totals.all.count} รายการ · ก่อนหัก WHT</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            ภาษีหัก ณ ที่จ่ายรวม
          </div>
          <div className="value tnum">
            {fmt(totals.all.wht)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {totals.all.base > 0
              ? `${((totals.all.wht / totals.all.base) * 100).toFixed(2)}% ของฐาน`
              : '— ของฐาน'}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            ภ.ง.ด. 53 (นิติบุคคล)
          </div>
          <div className="value tnum">
            {fmt(totals.f53.wht)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{totals.f53.count} ฉบับ · ใหญ่ที่สุด</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            รออนุมัติออกหนังสือ
          </div>
          <div className="value tnum">
            {totals.pending.count} <span className="unit">ฉบับ</span>
          </div>
          <div className="trend down">รวม {fmt(totals.pending.wht)} บาท</div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'certs' && (
        <WHTCertList
          data={filtered}
          allCount={allCerts.length}
          loading={certsQuery.isLoading}
          q={q}
          setQ={setQ}
          formF={formF}
          setFormF={setFormF}
          statusF={statusF}
          setStatusF={setStatusF}
          onPrintPdf={handlePrintPdf}
          onConfirmRemittance={handleConfirmRemit}
        />
      )}
      {tab === 'pnd3' && (
        <PNDForm
          form="3"
          data={filingCerts.filter((r) => r.form === '3')}
          branches={branches}
          filings={filingsQuery.data ?? []}
          period={period}
          onFile={() => handleFile('3')}
          onFileBranches={() => handleFileBranches('3')}
          onExportTxt={() => handleExportTxt('3')}
          fileBranchesPending={fileBranches.isPending}
        />
      )}
      {tab === 'pnd53' && (
        <PNDForm
          form="53"
          data={filingCerts.filter((r) => r.form === '53')}
          branches={branches}
          filings={filingsQuery.data ?? []}
          period={period}
          onFile={() => handleFile('53')}
          onFileBranches={() => handleFileBranches('53')}
          onExportTxt={() => handleExportTxt('53')}
          fileBranchesPending={fileBranches.isPending}
        />
      )}
      {tab === 'pnd54' && (
        <PNDForm
          form="54"
          data={filingCerts.filter((r) => r.form === '54')}
          branches={branches}
          filings={filingsQuery.data ?? []}
          period={period}
          onFile={() => handleFile('54')}
          onFileBranches={() => handleFileBranches('54')}
          onExportTxt={() => handleExportTxt('54')}
          fileBranchesPending={fileBranches.isPending}
        />
      )}
      {tab === 'rates' && <WHTRates />}

      {showIssue && (
        <WHTIssueModal
          companyId={companyId}
          onClose={() => setShowIssue(false)}
          onSubmit={(payload) =>
            createCert.mutate(payload, { onSuccess: () => setShowIssue(false) })
          }
          pending={createCert.isPending}
        />
      )}
    </div>
  );
}

/* ===== Cert List ===== */
interface WHTCertListProps {
  data: readonly UiCert[];
  allCount: number;
  loading: boolean;
  q: string;
  setQ: (v: string) => void;
  formF: 'all' | UiForm;
  setFormF: (v: 'all' | UiForm) => void;
  statusF: 'all' | UiStatus;
  setStatusF: (v: 'all' | UiStatus) => void;
  onPrintPdf: (certId: string) => void;
  // B5: confirm-remittance handler for e-WHT certs
  onConfirmRemittance: (
    certId: string,
    data: { bankRemittanceRef: string; bankConfirmedAt: string; cashAccountCode: string },
  ) => void;
}

function WHTCertList({
  data,
  allCount,
  loading,
  q,
  setQ,
  formF,
  setFormF,
  statusF,
  setStatusF,
  onPrintPdf,
  onConfirmRemittance,
}: WHTCertListProps) {
  const baseTotal = data.reduce((a, r) => a + r.base, 0);
  const whtTotal = data.reduce((a, r) => a + r.wht, 0);

  // B5: confirm-remittance inline form state
  const [remitId, setRemitId] = useState<string | null>(null);
  const [remitRef, setRemitRef] = useState('');
  const [remitDate, setRemitDate] = useState('');
  const [remitCash, setRemitCash] = useState('');

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <input
          className="input search w-lg"
          placeholder="ค้นหา เลขที่หนังสือ / ชื่อผู้ขาย / เลขผู้เสียภาษี"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            paddingLeft: 32,
            backgroundImage: SEARCH_BG,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '10px center',
          }}
        />
        <select
          className="select"
          value={formF}
          onChange={(e) => setFormF(e.target.value as 'all' | UiForm)}
        >
          <option value="all">แบบยื่น: ทั้งหมด</option>
          <option value="3">ภ.ง.ด. 3 (บุคคลธรรมดา)</option>
          <option value="53">ภ.ง.ด. 53 (นิติบุคคล)</option>
          <option value="54">ภ.ง.ด. 54 (จ่ายต่างประเทศ)</option>
        </select>
        <select className="select" defaultValue="all">
          <option value="all">ประเภทเงินได้: ทั้งหมด</option>
          {WHT_RATES.map((r) => (
            <option key={r.code} value={r.code}>
              {r.label}
            </option>
          ))}
        </select>
        <select
          className="select"
          value={statusF}
          onChange={(e) => setStatusF(e.target.value as 'all' | UiStatus)}
        >
          <option value="all">สถานะ: ทั้งหมด</option>
          <option value="issued">ออกแล้ว</option>
          <option value="pending">รออนุมัติ</option>
          <option value="filed">ยื่นแล้ว</option>
          <option value="void">ยกเลิก</option>
        </select>
        <input className="input w-sm" type="date" defaultValue="2026-05-01" />
        <span className="faint">ถึง</span>
        <input className="input w-sm" type="date" defaultValue="2026-05-31" />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className="btn sm">
            <Icon name="filter" size={12} /> ตัวกรอง
          </button>
        </div>
      </div>

      <div className="tbl-wrap" style={{ maxHeight: 560 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input type="checkbox" />
              </th>
              <th style={{ width: 140 }}>เลขที่หนังสือ</th>
              <th style={{ width: 95 }}>วันที่จ่าย</th>
              <th>ผู้ขาย / ผู้รับเงิน</th>
              <th>เลขประจำตัวผู้เสียภาษี</th>
              <th>ประเภทเงินได้</th>
              <th className="num">ฐานเงิน</th>
              <th className="num">อัตรา</th>
              <th className="num">ภาษีหัก ณ ที่จ่าย</th>
              <th>แบบ</th>
              <th>อ้างอิง</th>
              <th>สถานะ</th>
              <th style={{ width: 120 }}>เอกสาร</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={13} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  กำลังโหลด…
                </td>
              </tr>
            )}
            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={13} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีหนังสือรับรองในงวดที่เลือก
                </td>
              </tr>
            )}
            {data.map((r) => (
              <Fragment key={r.id}>
                <tr>
                  <td>
                    <input type="checkbox" />
                  </td>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{r.voucherNo}</a>
                  </td>
                  <td>{fmtDate(r.date)}</td>
                  <td>
                    <div>{r.vendor}</div>
                    {r.indiv && (
                      <div className="meta" style={{ fontSize: 14 }}>
                        บุคคลธรรมดา
                      </div>
                    )}
                  </td>
                  <td className="code muted">{r.taxId}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span className="code muted" style={{ fontSize: 14 }}>
                        {r.cat}
                      </span>
                      <span>{r.label}</span>
                    </span>
                  </td>
                  <td className="num tnum">{fmt(r.base)}</td>
                  <td className="num">
                    <b>{r.rate.toFixed(1)}%</b>
                  </td>
                  <td className="num tnum" style={{ color: 'var(--brand)', fontWeight: 600 }}>
                    {fmt(r.wht)}
                  </td>
                  <td>
                    {r.form === '3' && (
                      <span className="badge info">
                        <span className="bdot" />
                        ภ.ง.ด. 3
                      </span>
                    )}
                    {r.form === '53' && (
                      <span className="badge brand">
                        <span className="bdot" />
                        ภ.ง.ด. 53
                      </span>
                    )}
                    {r.form === '54' && (
                      <span className="badge warning">
                        <span className="bdot" />
                        ภ.ง.ด. 54
                      </span>
                    )}
                  </td>
                  <td className="code muted">{r.paymentRef}</td>
                  <td>
                    {r.status === 'issued' && (
                      <span className="badge positive">
                        <span className="bdot" />
                        ออกแล้ว
                      </span>
                    )}
                    {r.status === 'pending' && (
                      <span className="badge warning">
                        <span className="bdot" />
                        รออนุมัติ
                      </span>
                    )}
                    {r.status === 'filed' && (
                      <span className="badge info">
                        <span className="bdot" />
                        ยื่นแล้ว
                      </span>
                    )}
                    {r.status === 'void' && (
                      <span className="badge negative">
                        <span className="bdot" />
                        ยกเลิก
                      </span>
                    )}
                  </td>
                  <td style={{ minWidth: 180 }}>
                    {r.channel === 'E_WHT' ? (
                      r.bankConfirmedAt ? (
                        <span className="meta" style={{ fontSize: 13 }}>
                          นำส่งแล้ว ({r.bankRemittanceRef})
                        </span>
                      ) : (
                        <button
                          className="btn sm"
                          onClick={() => {
                            if (remitId === r.id) {
                              setRemitId(null);
                            } else {
                              setRemitId(r.id);
                              setRemitRef('');
                              setRemitDate('');
                              setRemitCash('');
                            }
                          }}
                        >
                          <Icon name="check" size={12} /> ยืนยันนำส่ง e-WHT
                        </button>
                      )
                    ) : (
                      <button className="btn sm ghost" onClick={() => onPrintPdf(r.id)}>
                        <Icon name="print" size={12} /> พิมพ์ 50 ทวิ
                      </button>
                    )}
                  </td>
                </tr>
                {remitId === r.id && (
                  <tr>
                    <td colSpan={13} style={{ background: '#fafafa', padding: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: 12,
                          alignItems: 'flex-end',
                          padding: '8px 16px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <label style={{ fontSize: 13 }}>
                          เลขที่อ้างอิงธนาคาร
                          <input
                            className="input"
                            value={remitRef}
                            onChange={(e) => setRemitRef(e.target.value)}
                            style={{ display: 'block', marginTop: 4 }}
                          />
                        </label>
                        <label style={{ fontSize: 13 }}>
                          วันที่ธนาคารนำส่ง
                          <input
                            className="input"
                            type="date"
                            value={remitDate}
                            onChange={(e) => setRemitDate(e.target.value)}
                            style={{ display: 'block', marginTop: 4 }}
                          />
                        </label>
                        <label style={{ fontSize: 13 }}>
                          บัญชีธนาคาร (จ่าย)
                          <input
                            className="input"
                            value={remitCash}
                            onChange={(e) => setRemitCash(e.target.value)}
                            style={{ display: 'block', marginTop: 4 }}
                          />
                        </label>
                        <button
                          className="btn primary sm"
                          disabled={!remitRef || !remitDate || !remitCash}
                          onClick={() => {
                            onConfirmRemittance(r.id, {
                              bankRemittanceRef: remitRef,
                              bankConfirmedAt: remitDate,
                              cashAccountCode: remitCash,
                            });
                            setRemitId(null);
                            setRemitRef('');
                            setRemitDate('');
                            setRemitCash('');
                          }}
                        >
                          ยืนยันการนำส่ง
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {data.length > 0 && (
              <tr className="total">
                <td colSpan={6}>รวม {data.length} ฉบับ</td>
                <td className="num tnum">{fmt(baseTotal)}</td>
                <td />
                <td className="num tnum" style={{ color: 'var(--brand)' }}>
                  {fmt(whtTotal)}
                </td>
                <td colSpan={4} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="tbl-footer">
        <span>
          แสดง {data.length} จาก {allCount} ฉบับ · ทุกฉบับลงบัญชี 20170011 — ภาษีหัก ณ ที่จ่าย
          ค้างจ่าย
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn sm">
            <Icon name="print" size={12} /> พิมพ์เลือก
          </button>
          <button className="btn sm">
            <Icon name="download" size={12} /> Export Excel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== ภ.ง.ด. form preview ===== */
interface PNDFormProps {
  form: UiForm;
  data: readonly UiCert[];
  branches: readonly Branch[];
  filings: readonly WhtFiling[];
  period: string;
  onFile: () => void;
  onFileBranches: () => void;
  onExportTxt: () => void;
  fileBranchesPending: boolean;
}

interface ByBranchBucket {
  branchId: string | null;
  taxBranch: string;
  nameTh: string;
  count: number;
  base: number;
  wht: number;
}

function PNDForm({
  form,
  data,
  branches,
  filings,
  period,
  onFile,
  onFileBranches,
  onExportTxt,
  fileBranchesPending,
}: PNDFormProps) {
  const cfg = PND_CFGS[form];

  // Per-branch filings already persisted for this (period, formType), keyed by
  // branchId so each branch subtotal row can show its filing status.
  const formType = UI_TO_FORM[form];
  const branchFilingByBranchId = new Map(
    filings
      .filter((f) => f.period === period && f.formType === formType && f.branchId)
      .map((f) => [f.branchId!, f]),
  );
  const hasTaggedBranch = data.some((r) => r.branchId);

  const totalBase = data.reduce((a, r) => a + r.base, 0);
  const totalWht = data.reduce((a, r) => a + r.wht, 0);
  const surcharge = 0;

  const byType: Record<string, ByTypeBucket> = {};
  data.forEach((r) => {
    const bucket = byType[r.cat] ?? { label: r.label, count: 0, base: 0, wht: 0 };
    bucket.count += 1;
    bucket.base += r.base;
    bucket.wht += r.wht;
    byType[r.cat] = bucket;
  });

  // Per-tax-branch breakdown — Thai WHT is filed per tax-branch, so we subtotal
  // the period's certs by their branchId (resolved to the 5-digit taxBranch code).
  const byBranch: Record<string, ByBranchBucket> = {};
  data.forEach((r) => {
    const key = r.branchId ?? '__head__';
    const b = r.branchId ? branches.find((x) => x.id === r.branchId) : undefined;
    const bucket = byBranch[key] ?? {
      branchId: r.branchId,
      taxBranch: b?.taxBranch ?? '00000',
      nameTh: b?.nameTh ?? 'สำนักงานใหญ่',
      count: 0,
      base: 0,
      wht: 0,
    };
    bucket.count += 1;
    bucket.base += r.base;
    bucket.wht += r.wht;
    byBranch[key] = bucket;
  });
  const branchRows = Object.values(byBranch).sort((a, b) => a.taxBranch.localeCompare(b.taxBranch));

  // Filer header line — single tax-branch when all certs share one, else a
  // multi-branch hint (full per-branch subtotals appear in the table below).
  const filerBranchLine =
    branchRows.length === 1 && branchRows[0]
      ? branchHeader(branchRows[0].branchId, branches)
      : branchRows.length > 1
        ? `${branchRows.length} สาขา (ดูสรุปแยกสาขาด้านล่าง)`
        : 'สาขาที่ 00000 (สำนักงานใหญ่)';

  return (
    <>
      {/* Form header card mimicking the official ภ.ง.ด. form */}
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>{cfg.title}</h3>
            <div className="sub">{cfg.sub} · ประจำเดือน พ.ค. 2026 · ยื่นภายใน 7 มิ.ย. 2026</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm">
              <Icon name="print" size={12} /> พิมพ์ใบแนบ
            </button>
            <button className="btn sm" onClick={onExportTxt}>
              <Icon name="download" size={12} /> Export Text File (RD)
            </button>
            <button className="btn sm primary" onClick={onFile}>
              <Icon name="upload" size={12} /> ยื่นออนไลน์
            </button>
          </div>
        </div>
        <div className="card-body">
          {/* Filer info */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr',
              gap: 14,
              padding: '10px 0 14px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div>
              <div className="meta">ผู้มีหน้าที่หักภาษี ณ ที่จ่าย</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>
                บริษัท ไดนาสตี้ เซรามิค จำกัด (มหาชน)
              </div>
              <div className="meta">เลขประจำตัวผู้เสียภาษี 0107536000625 · {filerBranchLine}</div>
            </div>
            <div>
              <div className="meta">เดือนภาษี / ปีภาษี</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>พฤษภาคม 2569 (2026)</div>
              <div className="meta">ยื่นปกติ · ครั้งที่ 1</div>
            </div>
            <div>
              <div className="meta">วันที่ยื่นแบบ</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>—</div>
              <div className="meta">กำหนดสุดท้าย 7 มิ.ย. 2026</div>
            </div>
          </div>

          {/* Summary by income type */}
          <div style={{ padding: '14px 0 4px' }}>
            <div className="section-title">สรุปตามประเภทเงินได้</div>
            <table className="tbl" style={{ border: '1px solid var(--border)', marginTop: 8 }}>
              <thead>
                <tr>
                  <th>รหัส</th>
                  <th>ประเภทเงินได้</th>
                  <th className="num">จำนวนราย</th>
                  <th className="num">ฐานเงินที่จ่าย</th>
                  <th className="num">ภาษีหัก ณ ที่จ่าย</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(byType).map(([k, v]) => (
                  <tr key={k}>
                    <td className="code">{k}</td>
                    <td>{v.label}</td>
                    <td className="num">{v.count}</td>
                    <td className="num tnum">{fmt(v.base)}</td>
                    <td className="num tnum">{fmt(v.wht)}</td>
                  </tr>
                ))}
                <tr className="total">
                  <td colSpan={2}>รวมทั้งสิ้น</td>
                  <td className="num">{data.length}</td>
                  <td className="num tnum">{fmt(totalBase)}</td>
                  <td className="num tnum" style={{ color: 'var(--brand)' }}>
                    {fmt(totalWht)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Summary by tax-branch — WHT is filed per สาขา (tax-branch) */}
          <div style={{ padding: '14px 0 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="section-title">สรุปแยกตามสาขา (ผู้หักภาษี)</div>
              <button
                className="btn sm primary"
                onClick={onFileBranches}
                disabled={fileBranchesPending || !hasTaggedBranch}
              >
                <Icon name="upload" size={12} /> ยื่นแยกสาขา
              </button>
            </div>
            <table className="tbl" style={{ border: '1px solid var(--border)', marginTop: 8 }}>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>รหัสสาขา</th>
                  <th>สาขา</th>
                  <th className="num">จำนวนราย</th>
                  <th className="num">ฐานเงินที่จ่าย</th>
                  <th className="num">ภาษีหัก ณ ที่จ่าย</th>
                  <th>สถานะการยื่น</th>
                </tr>
              </thead>
              <tbody>
                {branchRows.map((b) => {
                  const filing = b.branchId ? branchFilingByBranchId.get(b.branchId) : undefined;
                  return (
                    <tr key={b.branchId ?? '__head__'}>
                      <td className="code">{b.taxBranch}</td>
                      <td>{b.nameTh}</td>
                      <td className="num">{b.count}</td>
                      <td className="num tnum">{fmt(b.base)}</td>
                      <td className="num tnum">{fmt(b.wht)}</td>
                      <td>
                        {b.branchId === null ? (
                          <span className="badge neutral">ไม่ระบุสาขา</span>
                        ) : filing ? (
                          <span
                            className={`badge ${filing.status === 'FILED' ? 'positive' : 'brand'}`}
                          >
                            {filing.status === 'FILED' ? 'ยื่นแล้ว' : 'ร่าง'}
                          </span>
                        ) : (
                          <span className="badge neutral">ยังไม่ยื่น</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {branchRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 16 }}>
                      ไม่มีรายการในงวดนี้
                    </td>
                  </tr>
                )}
                {branchRows.length > 0 && (
                  <tr className="total">
                    <td colSpan={2}>รวมทุกสาขา</td>
                    <td className="num">{data.length}</td>
                    <td className="num tnum">{fmt(totalBase)}</td>
                    <td className="num tnum" style={{ color: 'var(--brand)' }}>
                      {fmt(totalWht)}
                    </td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Filing calc */}
          <div
            style={{
              padding: '14px 0 0',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 24,
            }}
          >
            <table className="tbl" style={{ border: '1px solid var(--border)' }}>
              <tbody>
                <tr>
                  <td>(1) ภาษีที่นำส่ง</td>
                  <td className="num tnum">{fmt(totalWht)}</td>
                </tr>
                <tr>
                  <td>(2) เงินเพิ่ม (ถ้ามี)</td>
                  <td className="num tnum">{fmt(surcharge)}</td>
                </tr>
                <tr className="total">
                  <td>รวมภาษีและเงินเพิ่มที่ต้องนำส่ง</td>
                  <td className="num tnum" style={{ color: 'var(--brand)' }}>
                    {fmt(totalWht + surcharge)}
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <div className="section-title">หมายเหตุสำคัญ</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div>
                  • ต้องยื่นภายในวันที่ <b style={{ color: 'var(--brand)' }}>7 มิ.ย. 2026</b>{' '}
                  หากเลยกำหนดเสีย
                  {form === '54' ? 'เงินเพิ่ม 1.5% ต่อเดือน' : 'ค่าปรับ + เงินเพิ่ม 1.5% ต่อเดือน'}
                </div>
                <div>
                  • ต้องออกหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) ให้ผู้ถูกหักภายในวันที่จ่าย
                </div>
                {form === '54' && (
                  <div>
                    • ผู้รับอยู่ในประเทศที่มีสัญญาภาษีซ้อน (Tax Treaty) อาจใช้อัตราที่ต่ำกว่า 15%
                    ได้
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail listing — like the form attachment */}
      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>ใบแนบ ภ.ง.ด. {form} — รายการรายตัว</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="meta">{data.length} รายการ</span>
            <button className="btn sm" onClick={onExportTxt}>
              <Icon name="download" size={12} /> Export TXT (RD format)
            </button>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 460 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 40 }}>ลำดับ</th>
                <th>{cfg.taxIdLabel}</th>
                <th>ชื่อผู้รับเงิน</th>
                <th>ที่อยู่</th>
                <th>วันที่จ่าย</th>
                <th>ประเภทเงินได้</th>
                <th className="num">ฐานเงินที่จ่าย</th>
                <th className="num">อัตรา</th>
                <th className="num">ภาษีหัก ณ ที่จ่าย</th>
                <th>เงื่อนไข</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={r.id}>
                  <td className="num">{i + 1}</td>
                  <td className="code">{r.taxId}</td>
                  <td>{r.vendor}</td>
                  <td className="muted">{r.addr}</td>
                  <td>{fmtDate(r.date)}</td>
                  <td>{r.label}</td>
                  <td className="num tnum">{fmt(r.base)}</td>
                  <td className="num">{r.rate.toFixed(1)}%</td>
                  <td className="num tnum">{fmt(r.wht)}</td>
                  <td className="muted" style={{ fontSize: 14 }}>
                    1 — หัก ณ ที่จ่าย
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td colSpan={6}>รวม</td>
                <td className="num tnum">{fmt(totalBase)}</td>
                <td />
                <td className="num tnum">{fmt(totalWht)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ===== WHT Rates reference ===== */
function WHTRates() {
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>อัตราภาษีหัก ณ ที่จ่าย — ตามประมวลรัษฎากร</h3>
          <div className="sub">
            อ้างอิงคำสั่งกรมสรรพากร ท.ป. 4/2528 และที่แก้ไขเพิ่มเติม · ใช้บังคับ พ.ศ. 2569
          </div>
        </div>
        <button className="btn sm">
          <Icon name="download" size={12} /> ดาวน์โหลด PDF
        </button>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 80 }}>รหัส</th>
              <th>ประเภทเงินได้ / รายการจ่าย</th>
              <th>รายละเอียด / กรณีตัวอย่าง</th>
              <th>มาตราอ้างอิง</th>
              <th className="num">อัตรานิติบุคคล</th>
              <th className="num">อัตราบุคคลธรรมดา</th>
              <th>แบบยื่น</th>
            </tr>
          </thead>
          <tbody>
            {WHT_RATES.map((r) => (
              <tr key={r.code}>
                <td className="code">
                  <b>{r.code}</b>
                </td>
                <td>
                  <b>{r.label}</b>
                </td>
                <td className="muted">{r.desc}</td>
                <td className="muted code" style={{ fontSize: 14 }}>
                  {r.income}
                </td>
                <td className="num">
                  <span className="badge brand" style={{ fontSize: 14 }}>
                    {r.rate.toFixed(1)}%
                  </span>
                </td>
                <td className="num">
                  {r.code === 'FOREIGN' ? (
                    <span className="meta">—</span>
                  ) : r.code === 'DIV' ? (
                    <span className="badge info" style={{ fontSize: 14 }}>
                      10%
                    </span>
                  ) : r.code === 'INT' ? (
                    <span className="badge info" style={{ fontSize: 14 }}>
                      15%
                    </span>
                  ) : (
                    <span className="badge info" style={{ fontSize: 14 }}>
                      {r.rate.toFixed(1)}%
                    </span>
                  )}
                </td>
                <td>
                  {r.form === '53' && (
                    <>
                      <span className="badge brand">ภ.ง.ด. 53</span>
                      {r.forIndiv === '3' && (
                        <span className="meta" style={{ marginLeft: 6, fontSize: 14 }}>
                          / ภ.ง.ด. 3
                        </span>
                      )}
                    </>
                  )}
                  {r.form === '54' && <span className="badge warning">ภ.ง.ด. 54</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div
        style={{
          padding: '14px 18px',
          background: '#fafafa',
          borderTop: '1px solid var(--border)',
          fontSize: 14,
          color: 'var(--text-muted)',
          lineHeight: 1.6,
        }}
      >
        <b style={{ color: 'var(--text)' }}>หมายเหตุ:</b> อัตราข้างต้นเป็นอัตรามาตรฐาน
        บางกรณีอาจได้รับยกเว้นหรือลดหย่อนตามอนุสัญญาภาษีซ้อน (Tax Treaty) เช่น Royalty ไป Italy
        ตามอนุสัญญาไทย-อิตาลี ใช้อัตรา 5-15% แทน 15% มาตรฐาน ·
        ค่าจ้างแรงงานพนักงานประจำใช้อัตราก้าวหน้าตามใบ ภ.ง.ด. 1 ไม่อยู่ในระบบนี้
        (ใช้ระบบเงินเดือนแยก)
      </div>
    </div>
  );
}

/* ===== Issue Cert Modal ===== */
interface WHTIssueModalProps {
  companyId: string | undefined;
  onClose: () => void;
  onSubmit: (payload: CreateWhtCertificate) => void;
  pending: boolean;
}

function WHTIssueModal({ companyId, onClose, onSubmit, pending }: WHTIssueModalProps) {
  const [type, setType] = useState<WhtIssueType>('corp');
  const [cat, setCat] = useState<string>('SVC');
  const [base, setBase] = useState<number>(100_000);
  const [vendorName, setVendorName] = useState<string>('');
  const [vendorTaxId, setVendorTaxId] = useState<string>('');
  const [vendorAddress, setVendorAddress] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');
  const [certDate, setCertDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [paymentRef, setPaymentRef] = useState<string>('');

  const rateInfo = getRateInfo(cat);
  const wht = Math.round(base * rateInfo.rate) / 100;
  const issueForm: UiForm = type === 'corp' ? '53' : type === 'foreign' ? '54' : '3';

  const canSubmit = !!vendorName && !!vendorTaxId && !!paymentRef && base > 0 && !pending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload: CreateWhtCertificate = {
      certDate,
      vendorName,
      vendorTaxId,
      ...(vendorAddress ? { vendorAddress } : {}),
      ...(branchId ? { branchId } : {}),
      paymentRef,
      formType: UI_TO_FORM[issueForm],
      incomeType: cat,
      incomeDesc: rateInfo.label,
      base,
      rate: rateInfo.rate,
      condition: 'WITHHELD',
    };
    onSubmit(payload);
  };

  const typeOptions: readonly (readonly [WhtIssueType, string, string])[] = [
    ['corp', 'นิติบุคคลในประเทศ', 'ภ.ง.ด. 53'],
    ['indiv', 'บุคคลธรรมดาในประเทศ', 'ภ.ง.ด. 3'],
    ['foreign', 'จ่ายไปต่างประเทศ', 'ภ.ง.ด. 54'],
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 780,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ออกหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ)
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              เลขที่ฉบับใหม่ <b style={{ color: '#3f3f46' }}>WHT-(ออกเลขเมื่อบันทึก)</b> ·
              จะออกตามแบบ ภ.ง.ด. {issueForm}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
          }}
        >
          {/* Type selector */}
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 8,
              }}
            >
              ประเภทผู้ถูกหักภาษี
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
              {typeOptions.map(([k, l, f]) => (
                <div
                  key={k}
                  onClick={() => setType(k)}
                  style={{
                    border: type === k ? '2px solid var(--brand)' : '1.5px solid var(--border)',
                    background: type === k ? 'var(--brand-tint)' : 'white',
                    borderRadius: 8,
                    padding: '12px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: type === k ? 600 : 500 }}>{l}</div>
                  <div className="meta">ยื่นแบบ {f}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Payee info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ชื่อผู้รับเงิน
              </label>
              <input
                className="input"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder={
                  type === 'indiv' ? 'เช่น นาย สมพร อินทรพานิช' : 'เช่น บจ. ABC โลจิสติกส์'
                }
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                {type === 'indiv'
                  ? 'เลขประจำตัวประชาชน'
                  : type === 'foreign'
                    ? 'Tax ID ในประเทศของผู้รับ'
                    : 'เลขประจำตัวผู้เสียภาษี'}
              </label>
              <input
                className="input code"
                value={vendorTaxId}
                onChange={(e) => setVendorTaxId(e.target.value)}
                placeholder="13 หลัก"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ที่อยู่
              </label>
              <input
                className="input"
                value={vendorAddress}
                onChange={(e) => setVendorAddress(e.target.value)}
                placeholder="ที่อยู่สำหรับหนังสือรับรอง"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                สาขา (ผู้หักภาษี)
              </label>
              <BranchSelect
                companyId={companyId}
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          {/* Income type */}
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 8,
              }}
            >
              ประเภทเงินได้ / ลักษณะรายการที่จ่าย
            </label>
            <select
              className="select"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              {WHT_RATES.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.code} — {r.label} (หัก {r.rate.toFixed(1)}%)
                </option>
              ))}
            </select>
            <div
              className="meta"
              style={{ marginTop: 6, paddingLeft: 10, borderLeft: '2px solid var(--brand-tint-2)' }}
            >
              {rateInfo.desc} · อ้างอิง {rateInfo.income}
            </div>
          </div>

          {/* Amount calculator */}
          <div
            style={{
              padding: '16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <div className="section-title">การคำนวณภาษี</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
                gap: 14,
                alignItems: 'center',
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  ฐานเงินที่จ่าย (ก่อน VAT)
                </label>
                <input
                  className="input tnum"
                  type="number"
                  value={base}
                  onChange={(e) => setBase(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 38,
                    fontSize: 15,
                    textAlign: 'right',
                    fontWeight: 600,
                  }}
                />
              </div>
              <div
                style={{
                  textAlign: 'center',
                  color: 'var(--text-faint)',
                  fontSize: 18,
                  marginTop: 18,
                }}
              >
                ×
              </div>
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  อัตรา
                </label>
                <div
                  style={{
                    height: 38,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'white',
                    border: '1px solid var(--border-strong)',
                    borderRadius: 6,
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'var(--brand)',
                  }}
                >
                  {rateInfo.rate.toFixed(1)}%
                </div>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  ภาษีหัก ณ ที่จ่าย
                </label>
                <div
                  style={{
                    height: 38,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'var(--brand)',
                    color: 'white',
                    borderRadius: 6,
                    fontSize: 17,
                    fontWeight: 700,
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(wht, 2)}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid var(--border)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 18,
                fontSize: 14,
              }}
            >
              <div>
                <span className="muted">ยอดก่อนหัก:</span> <b className="tnum">{fmt(base, 2)}</b>
              </div>
              <div>
                <span className="muted">หักภาษี:</span>{' '}
                <b className="tnum" style={{ color: 'var(--brand)' }}>
                  −{fmt(wht, 2)}
                </b>
              </div>
              <div>
                <span className="muted">จ่ายจริง:</span>{' '}
                <b className="tnum">{fmt(base - wht, 2)}</b>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่จ่าย
              </label>
              <input
                className="input"
                type="date"
                value={certDate}
                onChange={(e) => setCertDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                อ้างอิงใบสำคัญจ่าย
              </label>
              <input
                className="input code"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="PV-26050xxx"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 8,
              }}
            >
              เงื่อนไขการหัก
            </label>
            <div style={{ display: 'flex', gap: 14, fontSize: 14 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="cond" defaultChecked /> หัก ณ ที่จ่าย
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="cond" /> ออกภาษีให้ตลอดไป (Gross-up)
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="cond" /> ออกภาษีให้ครั้งเดียว
              </label>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn">
              <Icon name="print" size={14} /> ดูตัวอย่างหนังสือ
            </button>
            <button className="btn">บันทึก & ออกอีก</button>
            <button className="btn primary" onClick={handleSubmit} disabled={!canSubmit}>
              <Icon name="check" size={14} /> {pending ? 'กำลังบันทึก…' : 'ออกหนังสือรับรอง'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
